import React, { useState, useEffect, useContext, useMemo } from 'react'
import * as R from 'ramda'
import { PaymentOrder, Payment, AdjustmentType, PaymentStatus, PaymentAdjustment } from './model-payments'
import { Panel, Line, Line2, PanelAction } from './fragments/comp/panel'
import moment from 'moment'
import {
  AccountsCache,
  usePaymentsAPI,
  TransactionList,
  BalanceAccount,
  Transaction,
  Account,
} from './api/payments-api'
import { TenderType } from './model-payments'

import { Money } from 'views/order/components/utils'
import { RestaurantCtx, Restaurant } from 'views/order/v5/model-restaurant'
import { Order, OrderCtx } from './model-order'

import * as userStore from 'stores/user'

import '@babel/polyfill'
import { useController, useForm } from 'react-hook-form'
import { useAuditResourceService } from 'api/audit'
import { useHistory } from 'react-router'
import { AccessGroupApis } from './api/access-groups-api'
import { BillingScript, loadSubscriptionBillingPlan, RuleSet, ScriptRule } from './api/subscription'
import { useRestaurantFromOrder } from './edit-order/service'
import { useZupplerLogsAPI, ZupplerLogsButton, ZupplerLogsModal, ZupplerWorkflowEventLogs } from 'views/zuppler_logs'
import { Button } from 'reactstrap'

interface PaymentsInfoProps {
  orderId: string
  order: PaymentOrder
  reload: () => void
  data: Order
  skipAdjustments: boolean
}

type OrderAdjustmentType = 'chargeback' | 'chargeback-success' | 'credit-restaurant' | 'manual-adjustment'

type OrderAdjustment =
  | { type: 'chargeback'; amount: number; fee: number; description: string }
  | { type: 'chargeback-success'; amount: number; fee: number; description: string }
  | { type: 'credit-restaurant'; amount: number; description: string }
  | { type: 'manual-adjustment'; rules: ManualAdjustment[]; description: string }

interface OrderForm {
  type: 'full-refund'
  failedDelivery: boolean
  reason: string
}

interface ManualAdjustment {
  id: string
  names: string[]
  rule: ScriptRule
}

export default function Payments(props: PaymentsInfoProps): JSX.Element {
  const paymentsAPI = usePaymentsAPI()
  // const userInfo = `${userStore.name()} <${userStore.email()}>`
  const auditAPI = useAuditResourceService()
  const history = useHistory()

  const [transactionsList, setTransactions] = useState<TransactionList | null>()
  const [balanceAccounts, setBalanceAccounts] = useState<BalanceAccount[]>([])
  const [accounts, setAccounts] = useState<AccountsCache>({})
  const [adjustment, setAdjustment] = useState<PaymentAdjustment | null>(null)
  const [isSupportSupervisor, setIsSupportSupervisor] = useState(false)
  const [isBillingSupervisor, setIsBillingSupervisor] = useState(false)

  const restaurant = useContext(RestaurantCtx) as Restaurant
  const [manualAdjustments, setManualAdjustments] = useState<ManualAdjustment[]>([])
  const [billingScript, setBillingScript] = useState<BillingScript | null>(null)

  useEffect(() => {
    paymentsAPI.findOrderTransactions(props.orderId).then(setTransactions)
  }, [props.orderId, props.order])

  useEffect(() => {
    loadSubscriptionBillingPlan(restaurant).then((data: BillingScript) => {
      // console.log("Billing Plan", data)
      const rules = data.content.rule_set
        .filter((rs: RuleSet) => rs.applies_to.includes('manual adjustment'))
        .reduce((sum: ScriptRule[], rs: RuleSet) => [...sum, ...rs.rules], [])
        .map((rule: ScriptRule) => ({ id: rule.name, names: [rule.name, rule.dst_name], rule: rule }))
      setManualAdjustments(rules)
      setBillingScript(data)
    })
  }, [])

  useEffect(() => {
    if (transactionsList) {
      const accounts = new Set<number>()
      transactionsList.transactions.reduce((sum, { credit_account_id, debit_account_id }) => {
        sum.add(credit_account_id)
        sum.add(debit_account_id)
        return sum
      }, accounts)
      Promise.all(Array.from(accounts.values()).map(paymentsAPI.loadAccount)).then((res: Account[]) => {
        setAccounts(res.reduce((sum, a) => R.assoc('' + a.id, a, sum), {}))
        paymentsAPI.getOrderBalance(props.orderId, res).then(setBalanceAccounts)
      })
    }
  }, [transactionsList])

  useEffect(() => {
    Promise.all([
      AccessGroupApis.isAuthorized('support_supervisor', 'restaurant', String(props.data.carts.restaurant_id)),
      AccessGroupApis.isAuthorized('support_supervisor', 'channel', String(props.data.carts.channel_id)),
    ]).then((res) => {
      setIsSupportSupervisor(res[0].is_authorized || res[1].is_authorized)
    })
  }, [])

  useEffect(() => {
    Promise.all([
      AccessGroupApis.isAuthorized('billing_supervisor', 'restaurant', String(props.data.carts.restaurant_id)),
      AccessGroupApis.isAuthorized('billing_supervisor', 'channel', String(props.data.carts.channel_id)),
    ]).then((res) => {
      setIsBillingSupervisor(res[0].is_authorized || res[1].is_authorized)
    })
  }, [])

  const computeLimit = (payment: Payment, allPayments: Payment[]): number =>
    allPayments
      .filter((p) => p.parentPaymentId == payment.id)
      .reduce(
        (limit, p) =>
          p.type == 'charge' || p.type == 'adjustment'
            ? limit // + computeLimit(p, allPayments)
            : limit + computeLimit(p, allPayments),
        payment.amount
      )

  const onEditOrder = (payment: Payment) => {
    history.push(`/orders/${props.orderId}/edit/${payment.id}`)
  }

  const onCreatePayment = (type: AdjustmentType, payment: Payment) => {
    const limit = computeLimit(payment, props.order.payments)
    setAdjustment({
      id: 'new-payment',
      mode: type,
      parentPaymentId: payment.id,
      tip: 0,
      subtotal: 0,
      delivery: 0,
      notes: '',
      limit: type === 'charge' ? Number.MAX_VALUE : limit,
    })
  }

  const onCreateFullRefund = (orderForm: OrderForm) => {
    if (orderForm.failedDelivery) {
      paymentsAPI.createFullRefundChargeProvider(props.order.id).then(() => {
        auditAPI.createLog('CreateFullRefundChargeProvider', orderForm.reason)
        setOrderForm(null)
        props.reload()
      })
    } else {
      paymentsAPI.createFullRefund(props.order.id).then(() => {
        auditAPI.createLog('CreateFullRefund', orderForm.reason)
        setOrderForm(null)
        props.reload()
      })
    }
  }

  const onSaveAdjustment = (adj: PaymentAdjustment) => {
    paymentsAPI
      .createAdjustment({
        ...adj,
        notes: `${userStore.name()} (${userStore.email()}):(${((adj.subtotal || 0) / 100.0).toFixed(2)}/${(
          (adj.tip || 0) / 100.0
        ).toFixed(2)}/${((adj.delivery || 0) / 100.0).toFixed(2)}) - ${adj.notes}`,
      })
      .then(props.reload)
    setAdjustment(null)
  }

  const actions: PanelAction[] = []

  const [orderAdjustment, setOrderAdjustment] = useState<
    (OrderAdjustment & { onSave: (oa: OrderAdjustment) => Promise<Transaction[]>; onClose: () => void }) | null
  >(null)

  const onSaveOrderAdjustment = (orderAdjustment: OrderAdjustment) => {
    processAdjustment(orderAdjustment).then((transactions) => {
      auditAPI.createLog('CreateTransactions', JSON.stringify(transactions))
      paymentsAPI.findOrderTransactions(props.orderId).then(setTransactions)
      setOrderAdjustment(null)
    })
  }
  const processAdjustment = (orderAdjustment: OrderAdjustment) => {
    const restaurantAccount = R.values(accounts).find(
      (a: Account) => a.owner_type == 'restaurant' && a.type == 'balance'
    )
    const subscriptionAccount = R.values(accounts).find(
      (a: Account) => (a.owner_type == 'subscription' || a.owner_type == 'zuppler') && a.type == 'balance'
    )

    if (restaurantAccount && subscriptionAccount) {
      switch (orderAdjustment.type) {
        case 'chargeback':
          return Promise.all([
            paymentsAPI.createTransaction(
              'order',
              props.orderId,
              restaurantAccount,
              subscriptionAccount,
              orderAdjustment.amount,
              orderAdjustment.description + ' (chargeback amount)',
              'chargeback',
              'Chargeback Amount'
            ),
            paymentsAPI.createTransaction(
              'order',
              props.orderId,
              restaurantAccount,
              subscriptionAccount,
              orderAdjustment.fee,
              orderAdjustment.description + ' (chargeback fee)',
              'chargeback',
              'Chargeback Fee'
            ),
          ])
        case 'chargeback-success':
          return Promise.all([
            paymentsAPI.createTransaction(
              'order',
              props.orderId,
              subscriptionAccount,
              restaurantAccount,
              orderAdjustment.amount,
              orderAdjustment.description + ' (chargeback amount)',
              'chargeback success',
              'Chargeback Amount'
            ),
            paymentsAPI.createTransaction(
              'order',
              props.orderId,
              subscriptionAccount,
              restaurantAccount,
              orderAdjustment.fee,
              orderAdjustment.description + ' (chargeback fee)',
              'chargeback success',
              'Chargeback Fee'
            ),
          ])
        case 'credit-restaurant':
          return Promise.all([
            paymentsAPI.createTransaction(
              'order',
              props.orderId,
              subscriptionAccount,
              restaurantAccount,
              orderAdjustment.amount,
              orderAdjustment.description,
              'credit restaurant',
              'Manual Adjustment'
            ),
          ])
      }
    } else {
      alert('Cannot find any transactions between restaurant and subscription')
    }
    return Promise.reject('Cannot create adjustments')
  }

  const onCreateAdjustment = (type: OrderAdjustmentType): (() => void) => {
    const t = type as OrderAdjustment['type']
    return () => {
      if (!orderAdjustment || confirm('Do you wish to abandon current order adjustment?')) {
        setOrderAdjustment({ type: t, onSave: onSaveOrderAdjustment, onClose: () => setOrderAdjustment(null) })
      }
    }
  }

  const onCreateManualAdjustment = () => {
    if (!orderAdjustment || confirm('Do you wish to abandon current order adjustment?')) {
      setOrderAdjustment({
        type: 'manual-adjustment',
        onSave: onSaveManualOrderAdjustment,
        onClose: () => setOrderAdjustment(null),
      })
    }
  }

  const onSaveManualOrderAdjustment = (orderAdjustment: OrderAdjustment) => {
    if (orderAdjustment.type == 'manual-adjustment') {
      paymentsAPI
        .createNamedTransactions(
          props.orderId,
          orderAdjustment.rules.reduce((sum, r) => [...sum, ...r.names], [])
        )
        .then(({ transactions: t }) => {
          auditAPI.createLog(
            'Manual order adjustment',
            `${orderAdjustment.description}. Rules triggered: ${orderAdjustment.rules.map((r) => r.id).join(', ')}`
          )
          setOrderAdjustment(null)
          if (transactionsList) {
            const { pagination, transactions, nextPage } = transactionsList
            const res = [...t.map((x) => ({ ...x, highlight: true })), ...transactions]
            setTransactions({
              pagination,
              nextPage,
              transactions: res,
            })
            return Promise.resolve(res)
          } else {
            return paymentsAPI.findOrderTransactions(props.orderId).then(setTransactions)
          }
        })
    }
  }

  if (isBillingSupervisor) {
    actions.push({
      id: 'manual-adjustments',
      label: 'Manual Adjustments',
      style: 'unchecked',
      onClick: onCreateManualAdjustment,
    })
  }

  let OrderAdjustmentComp = null

  if (orderAdjustment) {
    OrderAdjustmentComp = orderAdjustmentEditorForType(orderAdjustment.type)
  }

  const [orderForm, setOrderForm] = useState<OrderForm | null>(null)
  const logsAPI = useZupplerLogsAPI()
  const orderActions: PanelAction[] = useMemo(() => {
    const actions: PanelAction[] = []
    if (
      (isSupportSupervisor && props.order.status == 'paid') ||
      props.order.status == 'pending' ||
      props.order.status == 'failed'
    ) {
      actions.push({
        id: 'add-refund-all',
        label: 'Full Refund',
        style: 'unchecked',
        onClick: () => {
          if (!orderForm) {
            setOrderForm({ type: 'full-refund', failedDelivery: false, reason: '' })
          }
        },
      })
    }

    if (isSupportSupervisor && logsAPI && logsAPI.hasLogs()) {
      actions.push({
        id: 'zuppler-logs',
        label: 'Logs',
        style: 'unchecked',
        onClick: logsAPI.showLogs,
      })
    }

    console.log('Actions', actions)
    return actions
  }, [isSupportSupervisor, props.order.status, logsAPI, logsAPI.hasLogs()])

  // console.log('HasLogs', logsAPI.hasLogs())
  // console.log('Log', logsAPI.getLogDetails())

  const allowEditOrder = props.data.workflow_id != null

  return (
    <div className="row">
      <div className="col-4">
        <Panel id="payments" label="Payment Informations" actions={orderActions}>
          <Line label="Payment Status">
            <span className={`badge badge-${paymentStatusToBadgeType(props.order.status)}`}>{props.order.status}</span>
          </Line>
          {orderForm && (
            <OrderFormUI
              {...orderForm}
              onSave={onCreateFullRefund}
              onClose={() => setOrderForm(null)}
              isSupportSupervisor={isSupportSupervisor}
            />
          )}
          <ZupplerLogsModal />
        </Panel>
        {props.order.payments
          .filter((p) => p.parentPaymentId == null)
          .map((p: Payment) => (
            <PaymentInfo
              key={p.id}
              payment={p}
              level={0}
              payments={adjustment ? [adjustment, ...props.order.payments] : props.order.payments}
              skipAdjustments={props.skipAdjustments}
              onEditOrder={allowEditOrder ? onEditOrder : null}
              onCreatePayment={onCreatePayment}
              onCancelAdjustment={() => {
                setAdjustment(null)
                props.reload()
              }}
              onSaveAdjustment={onSaveAdjustment}
              isSupportSupervisor={isSupportSupervisor}
            />
          ))}
      </div>
      <div className="col-8">
        <Panel id="transactions" label="Billing Transactions" actions={actions}>
          {OrderAdjustmentComp && billingScript && (
            <OrderAdjustmentComp
              {...orderAdjustment}
              balanceAccounts={balanceAccounts}
              accounts={accounts}
              manualAdjustments={manualAdjustments}
              billingScript={billingScript}
            />
          )}
          <Line2>
            {transactionsList ? (
              <TransactionsTable transactions={transactionsList.transactions} accounts={accounts} />
            ) : (
              <div>Loading transactions...</div>
            )}
          </Line2>
        </Panel>
        <Panel id="balance" label="Order Balance">
          <Line2>
            <table className="table table-compact table-striped">
              <thead>
                <tr>
                  <th>Acc. Name</th>
                  <th align="right">Credit</th>
                  <th align="right">Debit</th>
                  <th align="right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {balanceAccounts
                  .filter((ba: BalanceAccount) => ba.type == 'balance')
                  .map((ba: BalanceAccount, index: number) => (
                    <BalanceAccountLine key={index} {...ba} />
                  ))}
                {balanceAccounts
                  .filter((ba: BalanceAccount) => ba.type == 'principal')
                  .map((ba: BalanceAccount, index: number) => (
                    <BalanceAccountLine key={index} {...ba} />
                  ))}
              </tbody>
            </table>
          </Line2>
        </Panel>
      </div>
    </div>
  )
}

function tenderTypeToLabel(t: TenderType): string {
  switch (t) {
    case 'BRAINTREEINLINE2':
      return 'Braintree'
    default:
      return t
  }
}

function paymentTenderLabel(t: Payment): string {
  switch (t.tenderType) {
    case 'BRAINTREEINLINE2':
      return 'Braintree'
    case 'CASH':
      return t.account_id && t.account_id.trim().length ? 'House Account' : 'Cash'
    default:
      return tenderTypeToLabel(t.tenderType)
  }
}

function paymentStatusToBadgeType(s: PaymentStatus) {
  switch (s) {
    case 'paid':
      return 'success'
    case 'failed':
      return 'danger'
    case 'pending':
      return 'warning'
    default:
      return 'info'
  }
}

function paymentTypeToLabel(t: AdjustmentType, tenderType: TenderType, _tenderLabel: string) {
  switch (t) {
    case 'charge':
      return `Payment by ${tenderTypeToLabel(tenderType)}`
    case 'adjustment':
      return 'Adjustment'
    case 'refund':
      return 'Refund'
    case 'chargeback':
      return 'Chargeback'
  }
}

function isPayment(payment: Payment | PaymentAdjustment): payment is Payment {
  return (payment as PaymentAdjustment).mode === undefined
}

interface PaymentInfoProps {
  payment: Payment | PaymentAdjustment
  level: number
  payments: (Payment | PaymentAdjustment)[]
  onEditOrder: null | ((payment: Payment) => void)
  onCreatePayment: (type: AdjustmentType, payment: Payment) => void
  onCancelAdjustment: () => void
  onSaveAdjustment: (a: PaymentAdjustment) => void
  skipAdjustments: boolean
  isSupportSupervisor: boolean
}

function PaymentInfo(props: PaymentInfoProps): JSX.Element {
  const restaurant = useContext(RestaurantCtx) as Restaurant
  const actions: PanelAction[] = []
  const securityAllowed = props.isSupportSupervisor || userStore.hasRole('support')
  if (isPayment(props.payment)) {
    const paymentId = props.payment.id
    if (
      props.onEditOrder &&
      securityAllowed &&
      ['charge'].includes(props.payment.type) &&
      ['authorized', 'paid', 'pending', 'scheduled', 'capture_pending'].includes(props.payment.status) && // , 'failed', 'adjusted'
      !['LOYALTY'].includes(props.payment.tenderType)
    ) {
      actions.push({
        id: 'edit-order',
        label: 'Edit Order',
        style: 'unchecked',
        onClick: () => props.onEditOrder(props.payment as Payment),
      })
    }
    if (
      !props.onEditOrder &&
      securityAllowed &&
      ['charge', 'adjustment'].includes(props.payment.type) &&
      ['authorized', 'paid', 'pending', 'failed', 'adjusted'].includes(props.payment.status)
    ) {
      actions.push({
        id: 'charge',
        label: 'Add Charge',
        style: 'unchecked',
        onClick: () => props.onCreatePayment('charge', props.payment as Payment),
      })
    }
    if (
      !props.onEditOrder &&
      securityAllowed &&
      ['charge', 'adjustment'].includes(props.payment.type) &&
      ['pending', 'paid', 'authorized'].includes(props.payment.status)
    ) {
      actions.push({
        id: 'refund',
        label: 'Add Partial Refund',
        style: 'unchecked',
        onClick: () => props.onCreatePayment('refund', props.payment as Payment),
      })
    }
    return (
      <>
        <Panel
          id={props.payment.id.toString()}
          label={paymentTypeToLabel(props.payment.type, props.payment.tenderType, props.payment.tenderLabel) || ''}
          level={props.level}
          actions={props.skipAdjustments ? [] : actions}
          actionsTitle={actions.length > 1 ? 'Charges & Refunds' : undefined}>
          <Line label="Status">
            <span className={`badge badge-${paymentStatusToBadgeType(props.payment.status)}`}>
              {props.payment.status}
            </span>
          </Line>
          {props.payment.errorMessage && (
            <Line label="Reason">
              <span className="text-danger">{props.payment.errorMessage}</span>
            </Line>
          )}
          <Line label="Tender">
            {paymentTenderLabel(props.payment)} as {props.payment.tenderLabel}
          </Line>
          {props.payment.account_id && <Line label="House Account #">{props.payment.account_id}</Line>}
          <Line label="Customer Name">{props.payment.customerName}</Line>
          <Line label="Amount">
            <Money amount={props.payment.amount} locale={restaurant.locale} />
          </Line>
          {props.payment.note && <Line2>{props.payment.note}</Line2>}
          {props.payment.details && (
            <Line label="Card">
              {props.payment.details.cardType} ending {props.payment.details.cardLastDigits}{' '}
            </Line>
          )}
          {!!props.payment.externalTransactionId && (
            <Line label="Transaction ID">{props.payment.externalTransactionId}</Line>
          )}
          {props.payment.executedAt && (
            <Line label="Processed">
              <span title={props.payment.executedAt}>
                {moment(props.payment.executedAt)
                  .utcOffset(restaurant.timezone.offset)
                  .format('LLL')}
              </span>
            </Line>
          )}
        </Panel>
        {props.payments
          .filter((p) => p.parentPaymentId === paymentId)
          .map((p: Payment) => (
            <PaymentInfo
              key={p.id}
              payment={p}
              level={props.level + 1}
              payments={props.payments}
              onEditOrder={props.onEditOrder}
              onCreatePayment={props.onCreatePayment}
              onCancelAdjustment={props.onCancelAdjustment}
              onSaveAdjustment={props.onSaveAdjustment}
              skipAdjustments={props.skipAdjustments}
              isSupportSupervisor={props.isSupportSupervisor}
            />
          ))}
      </>
    )
  } else {
    return (
      <AdjustmentPaymentEditor
        payment={props.payment}
        level={props.level}
        onCancel={props.onCancelAdjustment}
        onSave={props.onSaveAdjustment}
      />
    )
  }
}

function AdjustmentPaymentEditor(props: {
  payment: PaymentAdjustment
  level: number
  onCancel: () => void
  onSave: (p: PaymentAdjustment) => void
}): JSX.Element {
  const [payment, setPayment] = useState(props.payment)
  const [errors, setErrors] = useState({ subtotal: [], tip: [], delivery: [], notes: [], general: [] })
  const isValid = (payment: PaymentAdjustment) => {
    const err = { subtotal: [], tip: [], delivery: [], notes: [], general: [] }
    let isValid = true
    const addError = (cat: 'subtotal' | 'tip' | 'delivery' | 'notes' | 'general', e: string) => {
      isValid = false
      err[cat].push(e)
    }

    if (payment.subtotal && (isNaN(payment.subtotal) || payment.subtotal < 0)) {
      addError('subtotal', 'Subtotal needs to be a valid positive number')
    }
    if (payment.delivery && (isNaN(payment.delivery) || payment.delivery < 0)) {
      addError('delivery', 'Delivery needs to be a valid positive number')
    }
    if (payment.tip && (isNaN(payment.tip) || payment.tip < 0)) {
      addError('tip', 'Tip needs to be a valid positive number')
    }
    if (!payment.subtotal && !payment.delivery && !payment.tip) {
      addError('general', 'At least one of subtotal, delivery or tip should be defined')
    }
    if (!payment.notes || payment.notes.trim().length == 0) {
      addError('notes', 'Please provide a reaons for this payment update')
    }
    if (payment.mode == 'refund' && payment.subtotal + payment.tip + payment.delivery > payment.limit) {
      addError('general', 'You cannot refund more than it was charged')
    }
    setErrors(err)
    return isValid
  }

  const convertToAdjustment = (p: PaymentAdjustment): PaymentAdjustment => {
    const { mode, tip, subtotal, delivery } = p
    switch (mode) {
      case 'charge':
        return p
      case 'refund':
        return { ...p, tip: -tip, subtotal: -subtotal, delivery: -delivery }
      default:
        throw `Unkown mode ${mode}`
    }
  }
  return (
    <Panel id={'adjustment-payment'} label={`Create ${props.payment.mode}`} level={props.level}>
      {props.payment.mode === 'refund' && (
        <Line2 highlight={payment.tip + payment.subtotal + payment.delivery > payment.limit}>
          Limit: {props.payment.limit / 100} Remaining:{' '}
          {(payment.limit - (payment.tip + payment.subtotal + payment.delivery)) / 100}
        </Line2>
      )}
      <Line2>
        {errors.general.map((err) => (
          <div className="text-danger">{err}</div>
        ))}
        <div className="row-fluid">
          <div className="form-group">
            <label htmlFor="subtotal" className="col-sm-2 control-label">
              Subtotal:
            </label>
            <div className="col-sm-10">
              <input
                id="subtotal"
                type="number"
                className="form-control"
                style={isNaN(payment.subtotal) ? { color: 'red' } : {}}
                min={0}
                onChange={(ev) => setPayment({ ...payment, subtotal: Math.round(parseFloat(ev.target.value) * 100.0) })}
              />
              {errors.subtotal.map((err) => (
                <div className="text-danger">{err}</div>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="tip" className="col-sm-2 control-label">
              Tip:
            </label>
            <div className="col-sm-10">
              <input
                id="tip"
                type="number"
                className="form-control"
                style={isNaN(payment.tip) ? { color: 'red' } : {}}
                min={0}
                onChange={(ev) => setPayment({ ...payment, tip: Math.round(parseFloat(ev.target.value) * 100.0) })}
              />
              {errors.tip.map((err) => (
                <div className="text-danger">{err}</div>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="delivery" className="col-sm-2 control-label">
              Delivery:
            </label>
            <div className="col-sm-10">
              <input
                id="delivery"
                type="number"
                className="form-control"
                style={isNaN(payment.delivery) ? { color: 'red' } : {}}
                min={0}
                onChange={(ev) => setPayment({ ...payment, delivery: Math.round(parseFloat(ev.target.value) * 100.0) })}
              />
              {errors.delivery.map((err) => (
                <div className="text-danger">{err}</div>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="reason" className="col-sm-12 control-label">
              Reason for {payment.mode}
            </label>
            <input
              type="text"
              id="reason"
              className="form-control col-sm-12"
              placeholder="...why this adjustment is requested and by whom?"
              onChange={(ev) => setPayment({ ...payment, notes: ev.target.value })}
            />
            {errors.notes && errors.notes.map((err) => <div className="text-danger">{err}</div>)}
          </div>
          <div className="button-group">
            <button className="btn btn-default" onClick={props.onCancel}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => (isValid(payment) ? props.onSave(convertToAdjustment(payment)) : null)}>
              Save
            </button>
          </div>
        </div>
      </Line2>
    </Panel>
  )
}

export function TransactionsTable(props: { transactions: Transaction[]; accounts: AccountsCache }): JSX.Element {
  return (
    <table className="table table-compact txn-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Debit</th>
          <th>Credit</th>
          <th>Description</th>
          <th align="right">Amount</th>
          <th>Statement</th>
        </tr>
      </thead>
      <tbody>
        {props.transactions
          .filter((t: Transaction) => t.amount !== 0)
          .map((t: Transaction) => (
            <TransactionLine key={t.id} {...t} accounts={props.accounts} />
          ))}
      </tbody>
    </table>
  )
}
export function TransactionLine(t: Transaction & { accounts: AccountsCache }): JSX.Element {
  const restaurant = useContext(RestaurantCtx) as Restaurant
  if (t.accounts['' + t.debit_account_id] && t.accounts['' + t.credit_account_id]) {
    const credit = t.accounts['' + t.credit_account_id]
    const debit = t.accounts['' + t.debit_account_id]
    return (
      <tr className={t.highlight ? 'txn-highlight' : ''}>
        <td className="txn-date">{moment(t.created_at).format('MMM D, HH:mm')}</td>
        <td>
          <span title={`${debit.owner_type}/${debit.type}`}>
            {debit.name} <span className="txn-acct-type">{debit.type}</span>
          </span>
        </td>
        <td>
          <span title={`${credit.owner_type}/${credit.type}`}>
            {credit.name} <span className="txn-acct-type">{credit.type}</span>
          </span>
        </td>
        <td className="txn-desc">
          {t.highlight && <i className="fa fa-check" />} {t.description}
        </td>
        <td align="right" className="txn-amount">
          ${(t.amount / 100).toFixed(2)}
        </td>
        <td>
          {t.invoice_id && (
            <a
              target="_blank"
              href={`${CHEF_URL}/#/onboarding/statements/${restaurant.id}/${t.invoice_id}/details`}
              title={`Invoice ${t.invoice_id}`}
              rel="noreferrer">
              <i className="fa fa-file-o" /> {t.invoice_id}
            </a>
          )}
        </td>
      </tr>
    )
  } else
    return (
      <tr>
        <td colSpan={3}>...</td>
        <td className="txn-desc">{t.description}</td>
        <td>
          <Money amount={t.amount} locale={restaurant.locale} />
        </td>
        <td colSpan={2}></td>
      </tr>
    )
}

function BalanceAccountLine(ba: BalanceAccount): JSX.Element {
  const restaurant = useContext(RestaurantCtx) as Restaurant
  return (
    <tr>
      <td>
        <span title={`${ba.owner_type}/${ba.type}`}>
          {ba.name} <span className="txn-acct-type">{ba.type}</span>
        </span>
      </td>
      <td align="right">
        <Money amount={ba.credit} locale={restaurant.locale} />
      </td>
      <td align="right">
        <Money amount={ba.debit} locale={restaurant.locale} />
      </td>
      <td align="right" className="txn-amount">
        <Money amount={ba.total} locale={restaurant.locale} />
      </td>
    </tr>
  )
}

function orderAdjustmentEditorForType(type: OrderAdjustmentType): (props: OrderAdjustmentProps) => JSX.Element {
  switch (type) {
    case 'chargeback':
      return ChargebackAdjustmentEditor
    case 'chargeback-success':
      return ChargebackSuccessAdjustmentEditor
    case 'credit-restaurant':
      return CreditRestaurantEditor
    case 'manual-adjustment':
      return ManualAdjustmentEditor
  }
}

interface OrderAdjustmentProps {
  balanceAccounts: BalanceAccount[]
  billingScript: BillingScript
  accounts: AccountsCache
  manualAdjustments: ManualAdjustment[]
  onSave: (oa: OrderAdjustment) => Promise<Transaction[]>
  onClose: () => void
}
function ChargebackAdjustmentEditor(props: OrderAdjustmentProps & OrderAdjustment): JSX.Element {
  const customerPrincipalAccount = props.balanceAccounts.find(
    (a: BalanceAccount) => a.type == 'principal' && a.owner_type == 'customer'
  )
  const { register, handleSubmit, watch, formState, getValues } = useForm({
    mode: 'onBlur',
    defaultValues: {
      amount: customerPrincipalAccount ? customerPrincipalAccount.debit / 100 : 0,
      fee: 0,
      description: '',
    },
  })

  const errors = formState.errors

  const onSubmit = (data: any) => {
    props.onSave({
      type: props.type,
      amount: parseFloat(data.amount),
      fee: parseFloat(data.fee),
      description: data.description,
    })
  }
  watch(['amount', 'fee', 'description'])
  const isValid = () => formState.isValid
  const modified = formState.isValid

  const chargebackDescription =
    props.type == 'chargeback'
      ? 'Amount & fee will debited from restaurant account and will be reflected in the next invoice.'
      : 'Amount & fee will be credited to the restaurant account and will be reflected in the next invoice.'

  const debitAccount = R.values(props.accounts).find(
    (a: Account) => a.owner_type == 'restaurant' && a.type == 'balance'
  )
  const creditAccount = R.values(props.accounts).find(
    (a: Account) => (a.owner_type == 'subscription' || a.owner_type == 'zuppler') && a.type == 'balance'
  )

  let transactions: Transaction[] = []
  if (debitAccount && creditAccount) {
    transactions = [
      {
        id: 1,
        amount: Math.round(parseFloat(getValues('amount')) * 100),
        credit_account_id: props.type == 'chargeback' ? creditAccount.id : debitAccount.id,
        debit_account_id: props.type == 'chargeback' ? debitAccount.id : creditAccount.id,
        applies_to: 'delivery order',
        description: getValues('description') + ' (chargeback amount)',
        billable_id: '',
        billable_type: 'order',
        created_at: '',
        invoice_id: null,
        billing_rule_name: '',
      },
      {
        id: 2,
        amount: Math.round(parseFloat(getValues('fee')) * 100),
        credit_account_id: props.type == 'chargeback' ? creditAccount.id : debitAccount.id,
        debit_account_id: props.type == 'chargeback' ? debitAccount.id : creditAccount.id,
        applies_to: 'delivery order',
        description: getValues('description') + ' (chargeback fee)',
        billable_id: '',
        billable_type: 'order',
        created_at: '',
        invoice_id: null,
        billing_rule_name: '',
      },
    ]
  }

  return (
    <Line2>
      <div className="row-fluid">
        <form className="form-horizontal" onSubmit={handleSubmit(onSubmit)}>
          <h4 style={{ textTransform: 'capitalize' }}>{props.type.replace(/-/, ' ')}</h4>
          <p className="text-muted">{chargebackDescription}</p>
          <div className="form-group">
            <label htmlFor="amount">Amount:</label>
            <input
              id="amount"
              className="form-control input input-small"
              style={errors.amount ? { color: 'red' } : {}}
              type="number"
              step={0.01}
              {...register('amount', {
                required: 'is required',
                min: { value: 1, message: 'must be greater than 0' },
              })}
            />
            {errors.amount && <span className="text-danger">{errors.amount.message}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="fee">Fee:</label>
            <input
              id="fee"
              className="form-control input input-small"
              style={errors.fee ? { color: 'red' } : {}}
              type="number"
              step={0.01}
              {...register('fee', {
                required: 'is required',
                min: { value: props.type == 'chargeback' ? 1 : 0, message: 'must be valid' },
              })}
            />
            {errors.fee && <span className="text-danger">{errors.fee.message}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="description">Description:</label>
            <input
              id="description"
              className="form-control input input-small"
              style={errors.description ? { color: 'red' } : {}}
              type="text"
              {...register('description', {
                required: 'is required',
              })}
            />
            {errors.description && <span className="text-danger">{errors.description.message}</span>}
          </div>

          <TransactionsTable transactions={transactions} accounts={props.accounts} />

          <div className="button-group">
            <a className="btn btn-default" onClick={props.onClose}>
              Cancel
            </a>
            <button className={modified ? 'btn btn-primary' : 'btn btn-default'} disabled={!isValid()} type="submit">
              Create Adjustment
            </button>
          </div>
        </form>
      </div>
    </Line2>
  )
}
const ChargebackSuccessAdjustmentEditor = ChargebackAdjustmentEditor

function CreditRestaurantEditor(props: OrderAdjustmentProps & OrderAdjustment): JSX.Element {
  const { register, handleSubmit, watch, formState, getValues } = useForm({
    mode: 'onBlur',
    defaultValues: {
      amount: 0,
      fee: 0,
      description: '',
    },
  })
  const errors = formState.errors
  const onSubmit = (data: any) => {
    return props.onSave({
      type: 'credit-restaurant',
      amount: parseFloat(data.amount),
      description: data.description,
    })
  }
  watch(['amount', 'description'])
  const isValid = () => formState.isValid
  const modified = formState.isValid

  const debitAccount = R.values(props.accounts).find(
    (a: Account) => a.owner_type == 'restaurant' && a.type == 'balance'
  )
  const creditAccount = R.values(props.accounts).find(
    (a: Account) => (a.owner_type == 'subscription' || a.owner_type == 'zuppler') && a.type == 'balance'
  )

  let transactions: Transaction[] = []
  if (debitAccount && creditAccount) {
    transactions = [
      {
        id: 1,
        amount: parseFloat(getValues('amount')) * 100,
        credit_account_id: debitAccount.id,
        debit_account_id: creditAccount.id,
        applies_to: 'delivery order',
        description: getValues('description'),
        billable_id: '',
        billable_type: 'order',
        created_at: '',
        invoice_id: null,
      },
    ]
  }

  return (
    <Line2>
      <div className="row-fluid">
        <form className="form-horizontal" onSubmit={handleSubmit(onSubmit)}>
          <h4 style={{ textTransform: 'capitalize' }}>{props.type.replace(/-/, ' ')}</h4>
          <p className="text-muted">
            Amount will be credited to the restaurant account and will be reflected in the next invoice
          </p>
          <div className="form-group">
            <label htmlFor="amount">Amount:</label>
            <input
              id="amount"
              className="form-control input input-small"
              style={errors.amount ? { color: 'red' } : {}}
              type="number"
              step={0.01}
              {...register('amount', {
                required: 'is required',
                min: { value: 1, message: 'must be greater than 0' },
              })}
            />
            {errors.amount && <span className="text-danger">{errors.amount.message}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="description">Description:</label>
            <input
              id="description"
              className="form-control input input-small"
              style={errors.description ? { color: 'red' } : {}}
              type="text"
              {...register('description', {
                required: 'is required',
              })}
            />
            {errors.description && <span className="text-danger">{errors.description.message}</span>}
          </div>

          <TransactionsTable transactions={transactions} accounts={props.accounts} />

          <div className="button-group">
            <a className="btn btn-default" onClick={props.onClose}>
              Cancel
            </a>
            <button className={modified ? 'btn btn-primary' : 'btn btn-default'} type="submit" disabled={!isValid()}>
              Create Adjustment
            </button>
          </div>
        </form>
      </div>
    </Line2>
  )
}

function OrderFormUI(
  props: OrderForm & { onSave: (orderForm: OrderForm) => void; onClose: () => void; isSupportSupervisor: boolean }
): JSX.Element {
  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors, isSubmitting, isLoading },
  } = useForm({
    mode: 'onBlur',
    defaultValues: { failedDelivery: false, reason: '' },
  })
  const onSubmit = (data: any) => {
    return props.onSave({
      type: props.type,
      failedDelivery: data.failedDelivery,
      reason: data.reason,
    })
  }
  const failedDelivery = watch('failedDelivery', false)
  const modified = isValid //formState.isValid

  return (
    <Line2>
      <div className="row-fluid">
        <form onSubmit={handleSubmit(onSubmit)}>
          <h4 style={{ textTransform: 'capitalize' }}>{props.type.replace(/-/, ' ')}</h4>
          <p className="text-muted">
            All the payments will be refunded with the amount charged - already existing refunds.
          </p>
          <div className="form-group">
            <div className="checkbox">
              <label htmlFor="failedDelivery" style={errors.failedDelivery ? { color: 'red' } : {}}>
                <input
                  id="failedDelivery"
                  className="checkbox"
                  type="checkbox"
                  step={0.01}
                  {...register('failedDelivery')}
                />{' '}
                Charge Service Provider for Order Failure:
              </label>
            </div>
            {errors.failedDelivery && <span className="text-danger">{errors.failedDelivery.message}</span>}
            {failedDelivery && (
              <span className="text-info">
                In addition to full refund the service provider will be charged acording with the rules defined on the
                subscription for failed delivery!
              </span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="reason">Reason:</label>
            <input
              id="reason"
              className="form-control input input-small"
              style={errors.reason ? { color: 'red' } : {}}
              type="text"
              {...register('reason', { required: 'is required' })}
            />
            {errors.reason && <span className="text-danger">{errors.reason.message}</span>}
          </div>

          <div className="button-group">
            <a className="btn btn-default" onClick={props.onClose}>
              Cancel
            </a>
            {props.isSupportSupervisor && (
              <Button
                className={modified ? 'btn btn-danger' : 'btn btn-default'}
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid || isLoading || isSubmitting}>
                Proceed with Full Refund
              </Button>
            )}
          </div>
        </form>
      </div>
    </Line2>
  )
}
interface ManualAdjustmentForm {
  adjustments: string[]
  reason: string
}
function ManualAdjustmentEditor(props: OrderAdjustmentProps & OrderAdjustment): JSX.Element {
  console.log('Billing Script', props.billingScript)
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<ManualAdjustmentForm>({
    mode: 'onBlur',
    defaultValues: {
      adjustments: [],
    },
  })
  const onSubmit = (data: ManualAdjustmentForm) => {
    if (data.adjustments.length > 0) {
      const mas = props.manualAdjustments.filter(({ id }) => data.adjustments.includes(id))
      props.onSave({ type: 'manual-adjustment', rules: mas, description: data.reason })
    }
  }
  const modified = isValid
  const rules = watch('adjustments')
  console.log('Selected rules', rules)

  // const selectedRules = useMemo(() => {
  //   const rulesFiltered = props.billingScript.content.rule_set
  //     .filter(rs => rs.applies_to == 'manual adjustment')
  //     // .filter(rs => R.intersection(rs.rules.map(r => r.name), rules).length > 0)
  //     .reduce((sum, rs) => [...sum, ...(rs.rules.filter(r => rules.includes(r.name)))], [])
  //   return rulesFiltered
  // }, [rules, props.billingScript])

  const renderPlanRule = (r: ScriptRule) => {
    let ruleAmount = ''
    if (r.fixed) {
      if (r.fixed.value.type == 'const') ruleAmount += `$${(r.fixed.value.value / 100).toFixed(2)}`
      else if (r.fixed.value.type == 'lookup') ruleAmount += `$${r.fixed.value.name}`
      ruleAmount += ` per ${r.fixed.count}`
    }
    if (r.percent) {
      if (r.percent.value.type == 'const') ruleAmount += `${r.percent.value.value / 100}%`
      if (r.percent?.value.type == 'lookup') ruleAmount += `${r.percent.value.name}%`
      ruleAmount += ` of ${r.percent.source}`
    }
    return (
      <>
        {r.debit.owner_type} -&gt; {r.credit.owner_type} = {ruleAmount}
      </>
    )
  }

  return (
    <Line2>
      <div className="row-fluid">
        <form className="form-horizontal" onSubmit={handleSubmit(onSubmit)}>
          <h4 style={{ textTransform: 'capitalize' }}>{props.type.replace(/-/, ' ')}</h4>
          <p className="text-muted">Please select which of the following manual adjustments do you want to trigger.</p>
          {props.manualAdjustments.map((ma) => (
            <div className="form-group checkbox" key={ma.id}>
              <label className="checkbox">
                <input type="checkbox" value={ma.id} {...register('adjustments')} /> <strong>{ma.id}</strong>:{' '}
                {renderPlanRule(ma.rule)}
              </label>
            </div>
          ))}
          <div className="form-group">
            <label htmlFor="reason">Reason:</label>
            <input
              id="reason"
              className="form-control input input-small"
              style={errors.reason ? { color: 'red' } : {}}
              type="text"
              {...register('reason', { required: 'is required' })}
            />
            {errors.reason && <span className="text-danger">{errors.reason.message}</span>}
          </div>
          <div className="button-group">
            <a className="btn btn-default" onClick={props.onClose}>
              Cancel
            </a>
            <button className={modified ? 'btn btn-primary' : 'btn btn-default'} type="submit" disabled={!isValid}>
              Create Manual Adjustment
            </button>
          </div>
        </form>
      </div>
    </Line2>
  )
}
